import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getTicketTypeById, getTicketTypeInfosByTicketTypeId } from "ticketino-api-client";
import parse from "html-react-parser";

import Header from "./components/Header";
import Footer from "./components/Footer";

const Checkout = () => {
    const [orderId] = useState(sessionStorage.getItem("OrderId"));
    const [token] = useState(sessionStorage.getItem("token"));

    const [order, setOrder] = useState({});
    const [tickets, setTickets] = useState([]);
    const [payment, setPayment] = useState({
        paymentMethodId: 0,
        deliveryMethodId: 0,
        insurance: false
    })

    const [submitError, setSubmitError] = useState("");

    // State for terms and conditions checkboxes
    const [termsAccepted, setTermsAccepted] = useState({
        agb1: false,
        agb2: false,
        agb3: false,
        agb4: false,
        privacyPolicyPointZero: false
    });

    const theEvent = JSON.parse(sessionStorage.getItem("SwissFinanceWeekEvent"));
    const [orderInsurancePrice, setOrderInsurancePrice] = useState(0);

    const [showCheckoutPrivacyPolice, setShowCheckoutPrivacyPolice] = useState([]);

    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching resources
    const [resources, setResources] = useState({});

    // fetching params
    const { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("fr" || "FR"):
            languageId = 2;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        case ("it" || "IT"):
            languageId = 4;
            break;
        default:
            languageId = 0;
            break;
    }

    useEffect(() => {
        // gtm - addPaymentInfo
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'addPaymentInfo'
        });
    }, [])

    useEffect(() => {
        requestResources();
        requestFormSettings();
    }, [language]); //everytime language is changed

    useEffect(() => {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        api.defaults.headers.common['Authorization'] = "Bearer " + token;

        if (orderId) {
            loadOrder(orderId);
            changeDeliveryType(1)
            getOrderInsurance();
        }
    }, [orderId]); //gets called when an order is started

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.checkOut);
            })
            .catch((error) => console.error(error.response.data));
    };

    const requestFormSettings = () => {
        axios.get(`form/formsettings`).then((res) => {
            setShowCheckoutPrivacyPolice(res.data.showCheckoutPrivacyPolice);
        });
    };

    const loadOrder = async (orderId) => {
        try {
            // Order
            const updatedOrder = await getOrderByOrderId(orderId);

            const bookedTickets = updatedOrder.tickets;

            // Booked Tickets
            const updatedTickets = await Promise.all(
                bookedTickets.map(async (ticket) => {
                    const ticketType = await getTicketTypeById(ticket.ticketTypeId);

                    const info = await getTicketTypeInfosByTicketTypeId(ticket.ticketTypeId);
                    const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

                    return { ...ticket, ticketType: ticketType, info: ticketTypeInfo }
                })
            )

            let updatedPayment = { ...payment, deliveryMethodId: updatedOrder.deliveryMethodId, paymentMethodId: updatedOrder.paymentMethodId };

            updatedPayment.insurance = updatedOrder.totalInsuranceAmount > 0;

            setPayment(updatedPayment);
            setOrder(updatedOrder);
            setTickets(updatedTickets);
        }
        catch (error) {
            console.error(error);
        }
    }

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        }
        catch (error) {
            console.error(error);
        }
    }

    const changeDeliveryType = async (deliveryMethodId) => {
        await axios.put(
            `${baseUrl}/ShopBasket/Order/${orderId}/DeliveryMethod/${deliveryMethodId}`)
            .then(() => {
                loadOrder(orderId);
            })
            .catch((error) => {
                console.error(error.response.data);
            });
    };

    const changePaymentType = (paymentMethodId) => {
        axios
            .put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/${paymentMethodId}`)
            .then((res) => {
                loadOrder(orderId);
            })
            .catch((error) => {
                console.error(error.response.data);
            });
    };

    const startDatatrans = () => {
        var baseRequestUrl = `https://datatrans.ticketino.com/Datatrans/${orderId}/DigitalSignature`;

        axios
            .get(baseRequestUrl)
            .then((res) => {
                let datatransFormId = "datatrans-" + new Date().getTime();
                var form =
                    "<form className='datatrans' id='" + datatransFormId + "'></form>";

                let container = document.getElementById("datatrans-form-placeholder");

                container.innerHTML += form;

                let element = document.getElementById(datatransFormId);

                // merchantId for testing
                // element.setAttribute("data-merchant-id", "1100004624");
                element.setAttribute("data-merchant-id", res.data.merchantId);
                element.setAttribute("data-amount", res.data.amount);
                element.setAttribute("data-currency", res.data.currency);
                element.setAttribute("data-refno", res.data.referenceNumber);
                element.setAttribute("data-reqType", res.data.reqType);
                element.setAttribute(
                    "data-upp-return-target",
                    res.data.uppReturnTarget
                );
                element.setAttribute("data-paymentMethod", res.data.paymentMethod);
                element.setAttribute("data-sign", res.data.digitalSignature);

                let domain = "https://" + window.location.host;

                let successUrl = domain + `/form/redirect/${language}/success/${orderId}`;
                let errorUrl = domain + `/form/redirect/${language}/error/${orderId}`;
                let cancelUrl = domain + `/form/redirect/${language}/cancel/${orderId}`;

                element.setAttribute("data-success-url", successUrl);
                element.setAttribute("data-error-url", errorUrl);
                element.setAttribute("data-cancel-url", cancelUrl);

                for (const key in res.data.userInfo) {
                    element.setAttribute(key, res.data.userInfo[key]);
                }

                for (const key in res.data.merchantSpecificParameters) {
                    element.setAttribute(key, res.data.merchantSpecificParameters[key]);
                }

                // start datatrans -> call the payment form
                window.Datatrans.startPayment({
                    form: "#" + datatransFormId,
                });
            })
            .catch((error) => {
                alert(error.response.data);
            });
    };

    // Format price with Swiss thousand separator (apostrophe)
    const formatPrice = (price) => {
        if (price == null) return "";
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    };

    // validate that the order still exists before payment process
    const validateOrderStillExists = async () => {
        try {
            const latest = await getOrderByOrderId(orderId);
            if (!latest) return false;
            if (!latest?.id) return false;
            if (!Array.isArray(latest?.tickets) || latest.tickets.length === 0) return false;
            return true;
        } catch (e) {
            return false;
        }
    }

    const onSubmit = async () => {

        setSubmitError("");

        // Validate that all mandatory terms are accepted
        if (!termsAccepted.agb1 || !termsAccepted.agb2 || !termsAccepted.agb4) {
            setSubmitError(resources?.PleaseAcceptTerms || "Please accept all terms and conditions to continue.");
            return;
        }

        // Validate privacy policy if required (mandatory when visible)
        if (shouldShowPrivacyPolicy() && !termsAccepted.privacyPolicyPointZero) {
            setSubmitError(resources?.PleaseAcceptTerms || "Please accept all terms and conditions to continue.");
            return;
        }

        const exists = await validateOrderStillExists();
        if (!exists) {
            setSubmitError(resources?.OrderExpiredMessage || "Your reservation is no longer available. Please start again.");
            return;
        }

        if (order?.totalPrice > 0) {
            let dataTransProviders = [1, 7, 8, 9, 10, 14, 11];

            let datatransProvider = dataTransProviders.findIndex((d) => d === payment.paymentMethodId) !== -1

            if (datatransProvider) {
                startDatatrans();
            } else {
                navigate(`/${language}/confirmation/${orderId}`);
            }
        } else {
            changePaymentTypeToFree();
        }
    }

    const getOrderInsurance = async () => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`);
            setOrderInsurancePrice(res.data);
        } catch (error) {
            console.error(error);
        }
    }

    const setOrderInsurance = async () => {
        await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                loadOrder(orderId);
            }).catch((error) => {
                console.error(error);
            })
    }

    const removeOrderInsurance = async () => {
        await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Insurance`)
            .then(() => {
                loadOrder(orderId);
            }).catch((error) => {
                console.error(error);
            })
    }

    const confirmShopbasketByOrderId = async (orderId) => {
        try {
            const res = await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/Confirm`);
            return res.data;
        } catch (error) {
            console.error(error)
        }
    };

    const changePaymentTypeToFree = () => {
        axios
            .put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/5`)
            .then(async () => {
                await confirmShopbasketByOrderId(orderId);
                navigate(`/${language}/confirmation/${orderId}`);
            });
    }

    const mapTickets = () => {
        let ticketsSorted = [];

        if (tickets && tickets.length > 0) {
            ticketsSorted = [...tickets].sort((a, b) => a.id - b.id);
        }

        return (
            tickets &&
            tickets.length > 0 &&
            ticketsSorted.map((tt, index) => (
                <div key={index} className="row pt-2 pb-2">
                    <div className="col-md-10 col-8">
                        <label className="fs-6">
                            {tt.info?.name}
                        </label>
                    </div>
                    <div className="col-md-2 col-4 text-end">
                        <label>
                            {tt.ticketType?.currency} {formatPrice(tt.price.toFixed(2))}
                        </label>
                    </div>
                </div>
            ))
        );
    }

    const mapExtraCosts = () => {
        return (
            <div>
                <div className="row pt-2 pb-2">
                    <div className="col-md-10 col-8">
                        <label className="fs-6">
                            Kosten für Lieferung und Zahlung
                        </label>
                    </div>
                    <div className="col-md-2 col-4 text-end">
                        <label>
                            {order.currency + " " + order?.deliverPaymentFee.toFixed(2)}
                        </label>
                    </div>
                </div>
            </div>
        )
    }

    const mapInsurance = () => {
        return (
            <div>
                <div className="row pt-2 pb-2">
                    <div className="col-md-10 col-8">
                        <label className="fs-6">
                            {resources.TicketInsurance}
                        </label>
                    </div>
                    <div className="col-md-2 col-4 text-end">
                        <label>
                            {order.currency + " " + order.totalInsuranceAmount.toFixed(2)}
                        </label>
                    </div>
                </div>
            </div>
        )
    }

    // Check if any ticket should show privacy policy
    const shouldShowPrivacyPolicy = () => {
        if (!tickets || tickets.length === 0 || !showCheckoutPrivacyPolice || showCheckoutPrivacyPolice.length === 0) {
            return false;
        }
        return tickets.some(ticket => showCheckoutPrivacyPolice.includes(ticket.ticketTypeId));
    }

    return (
        <>
            <div id="datatrans-form-placeholder"></div>

            <Header />

            <div className="container pt-0 wrapper-smaller">
                <>


                    <div>
                        {order.totalPrice > 0 && <>

                            <div className="row mt-5">
                                <div className="col-md-12">
                                    <span className="title blue">PAYMENT</span><br />
                                    <span className="text-muted">Please select your preferred payment method</span>
                                </div>
                            </div>

                            <div className="mt-4 mb-4">
                                {/* Mastercard */}
                                <div className="mb-3">
                                    <label className="d-flex align-items-center radio-custom" style={{ cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="flexRadioDefault"
                                            checked={payment.paymentMethodId === 1}
                                            onChange={() => changePaymentType(1)}
                                            className="d-none"
                                            id="mastercard"
                                        />
                                        <span className="radio-icon me-2">
                                            <i className="bi bi-circle radio-circle"></i>
                                            {payment.paymentMethodId === 1 && <i className="bi bi-check-lg radio-check"></i>}
                                        </span>
                                        <span className="fs-6 bold">Mastercard</span>
                                    </label>
                                </div>

                                {/* Visa */}
                                <div className="mb-3 mt-2">
                                    <label className="d-flex align-items-center radio-custom" style={{ cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="flexRadioDefault"
                                            checked={payment.paymentMethodId === 7}
                                            onChange={() => changePaymentType(7)}
                                            className="d-none"
                                            id="visa"
                                        />
                                        <span className="radio-icon me-2">
                                            <i className="bi bi-circle radio-circle"></i>
                                            {payment.paymentMethodId === 7 && <i className="bi bi-check-lg radio-check"></i>}
                                        </span>
                                        <span className="fs-6 bold">Visa</span>
                                    </label>
                                </div>

                                {/* American Express */}
                                <div className="mb-3 mt-2">
                                    <label className="d-flex align-items-center radio-custom" style={{ cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="flexRadioDefault"
                                            checked={payment.paymentMethodId === 8}
                                            onChange={() => changePaymentType(8)}
                                            className="d-none"
                                            id="amex"
                                        />
                                        <span className="radio-icon me-2">
                                            <i className="bi bi-circle radio-circle"></i>
                                            {payment.paymentMethodId === 8 && <i className="bi bi-check-lg radio-check"></i>}
                                        </span>
                                        <span className="fs-6 bold">American Express</span>
                                    </label>
                                </div>

                                {/* Twint */}
                                <div className="mb-3 mt-2">
                                    <label className="d-flex align-items-center radio-custom" style={{ cursor: "pointer" }}>
                                        <input
                                            type="radio"
                                            name="flexRadioDefault"
                                            checked={payment.paymentMethodId === 14}
                                            onChange={() => changePaymentType(14)}
                                            className="d-none"
                                            id="twint"
                                        />
                                        <span className="radio-icon me-2">
                                            <i className="bi bi-circle radio-circle"></i>
                                            {payment.paymentMethodId === 14 && <i className="bi bi-check-lg radio-check"></i>}
                                        </span>
                                        <span className="fs-6 bold">Twint</span>
                                    </label>
                                </div>
                            </div>
                        </>
                        }

                        {order.totalPrice > 0 && orderInsurancePrice > 0 &&
                            <div className="row">
                                <div className="col-md-12 mb-2">
                                    <h1 className="fs-5 red">{resources.TicketInsurance}</h1>
                                </div>
                                <div className="col-md-12 mb-3">
                                    <div className="form-check mb-4">
                                        <input
                                            className="form-check-input me-3 "
                                            type="checkbox"
                                            checked={payment.insurance}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setOrderInsurance();
                                                } else {
                                                    removeOrderInsurance();
                                                }
                                            }}
                                        />
                                        <label className="col-11 form-check-label fs-6" htmlFor="inlineCheckbox1">
                                            {parse(
                                                (orderInsurancePrice === 0
                                                    ? resources.TicketInsuranceDescGeneric
                                                    : (resources.TicketInsuranceDesc ?? "").replace("{@InsurancePrice}", orderInsurancePrice.toFixed(2))
                                                ) ?? ""
                                            )}
                                        </label>
                                    </div>

                                </div>
                            </div>}

                        <div className="row mt-5">
                            <div className="col-md-12">
                                <span className="title blue">Summary</span><br />
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-md-12 mb-1">
                                {mapTickets()}
                                {payment.insurance && mapInsurance()}
                                {order.deliverPaymentFee > 0 && mapExtraCosts()}
                                <hr className="custom-hr"></hr>
                                <div className="row mb-3">
                                    <div className="col-md-10 col-8">
                                        <label className="fs-6 fw-bold">
                                            Total incl. 8.1% VAT. :
                                        </label>
                                    </div>
                                    <div className="col-md-2 col-4 text-end">
                                        <label className="fw-bold">
                                            {order.currency} {formatPrice(order.totalPrice?.toFixed(2))}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Terms and Conditions Checkboxes */}
                        <div className="row mt-5">
                            <div className="col-md-12">
                                <div className="mb-4">
                                    <div className="mb-3">
                                        <label className="d-flex align-items-start checkbox-custom" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                id="agb1"
                                                checked={termsAccepted.agb1}
                                                onChange={(e) => setTermsAccepted({ ...termsAccepted, agb1: e.target.checked })}
                                                className="d-none"
                                            />
                                            <span className="checkbox-icon me-3 mt-1">
                                                <i className="bi bi-square checkbox-square"></i>
                                                {termsAccepted.agb1 && <i className="bi bi-check-lg checkbox-check"></i>}
                                            </span>
                                            <span className="form-check-label fs-6">
                                                {parse(resources?.AGB1 || "")}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Privacy Policy Point Zero Forum - Conditional and Mandatory */}
                                    {shouldShowPrivacyPolicy() && (
                                        <div className="mb-3">
                                            <label className="d-flex align-items-start checkbox-custom" style={{ cursor: "pointer" }}>
                                                <input
                                                    type="checkbox"
                                                    id="privacyPolicyPointZero"
                                                    checked={termsAccepted.privacyPolicyPointZero}
                                                    onChange={(e) => setTermsAccepted({ ...termsAccepted, privacyPolicyPointZero: e.target.checked })}
                                                    className="d-none"
                                                />
                                                <span className="checkbox-icon me-3 mt-1">
                                                    <i className="bi bi-square checkbox-square"></i>
                                                    {termsAccepted.privacyPolicyPointZero && <i className="bi bi-check-lg checkbox-check"></i>}
                                                </span>
                                                <span className="form-check-label fs-6">
                                                    {parse(resources?.PrivacyPolicyPointZeroForum || "")}
                                                </span>
                                            </label>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="d-flex align-items-start checkbox-custom" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                id="agb2"
                                                checked={termsAccepted.agb2}
                                                onChange={(e) => setTermsAccepted({ ...termsAccepted, agb2: e.target.checked })}
                                                className="d-none"
                                            />
                                            <span className="checkbox-icon me-3 mt-1">
                                                <i className="bi bi-square checkbox-square"></i>
                                                {termsAccepted.agb2 && <i className="bi bi-check-lg checkbox-check"></i>}
                                            </span>
                                            <span className="form-check-label fs-6">
                                                {parse(resources?.AGB2 || "")}
                                            </span>
                                        </label>
                                    </div>

                                    <div className="mb-3">
                                        <label className="d-flex align-items-start checkbox-custom" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                id="agb3"
                                                checked={termsAccepted.agb3}
                                                onChange={(e) => setTermsAccepted({ ...termsAccepted, agb3: e.target.checked })}
                                                className="d-none"
                                            />
                                            <span className="checkbox-icon me-3 mt-1">
                                                <i className="bi bi-square checkbox-square"></i>
                                                {termsAccepted.agb3 && <i className="bi bi-check-lg checkbox-check"></i>}
                                            </span>
                                            <span className="form-check-label fs-6">
                                                {parse(resources?.AGB3 || "")}
                                            </span>
                                        </label>
                                    </div>

                                    <div className="mb-3">
                                        <label className="d-flex align-items-start checkbox-custom" style={{ cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                id="agb4"
                                                checked={termsAccepted.agb4}
                                                onChange={(e) => setTermsAccepted({ ...termsAccepted, agb4: e.target.checked })}
                                                className="d-none"
                                            />
                                            <span className="checkbox-icon me-3 mt-1">
                                                <i className="bi bi-square checkbox-square"></i>
                                                {termsAccepted.agb4 && <i className="bi bi-check-lg checkbox-check"></i>}
                                            </span>
                                            <span className="form-check-label fs-6">
                                                {parse(resources?.AGB4 || "")}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-5">
                            <div className="col-md-6 text-start">
                                <button className="btn btn-custom" onClick={() => navigate(-1)}>
                                    {resources._Back}
                                </button>
                            </div>
                            <div className="col-md-6 text-end">
                                <button
                                    className="btn btn-custom"
                                    onClick={() => onSubmit()}
                                    disabled={
                                        (payment.paymentMethodId === 0 && payment.deliveryMethodId === 0) ||
                                        !termsAccepted.agb1 || 
                                        !termsAccepted.agb2 || 
                                        !termsAccepted.agb4 ||
                                        (shouldShowPrivacyPolicy() && !termsAccepted.privacyPolicyPointZero)
                                    }
                                >
                                    {resources.FinishOrder}
                                </button>
                            </div>
                        </div>

                        {submitError && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="alert alert-danger" role="alert">
                                        {submitError}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </>
            </div>
            {/* footer */}
            <div className="container wrapper">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <Footer language={language} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default Checkout